import React, { useState, useEffect } from 'react';
import { FaUser, FaArrowRight, FaLink, FaPlus, FaUserMd } from 'react-icons/fa';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { getAuth } from 'firebase/auth';
// Import organization utilities for multi-tenancy filtering
import { matchesOrganization as matchesOrg } from '../utils/organization';

const PatientInfoForm = ({ onSubmit, isSuccessWorkflow = false, activePractice = '', organization = 'alliedoms' }) => {
  // Use activePractice from props (from the practice toggle) for practice-specific filtering
  const [userRole, setUserRole] = useState('');
  const [patientInfo, setPatientInfo] = useState({
    patientID: '',
    procedureId: '',
    procedureType: '',
    procedureSelection: 'new', // 'new' or 'existing'
    physicianId: '',
    physicianName: ''
  });
  const [existingProcedures, setExistingProcedures] = useState([]);
  const [isLoadingProcedures, setIsLoadingProcedures] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [physicians, setPhysicians] = useState([]);
  const [isLoadingPhysicians, setIsLoadingPhysicians] = useState(false);

  // Get user role (still need this for admin checks)
  useEffect(() => {
    const fetchUserRole = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserRole(userData.role || '');
          }
        } catch (error) {
          console.error('Error fetching user info:', error);
        }
      }
    };
    fetchUserRole();
  }, []);

  // Fetch physicians for the currently active practice
  // Supports multi-practice physicians via 'practices' array
  useEffect(() => {
    const fetchPhysicians = async () => {
      // Use activePractice from props (from practice toggle)
      if (!activePractice && userRole !== 'System Administrator') {
        setPhysicians([]);
        return;
      }

      setIsLoadingPhysicians(true);
      try {
        const physiciansRef = collection(db, 'physicians');
        // Fetch all physicians and filter client-side for multi-practice support
        const physicianSnapshot = await getDocs(physiciansRef);
        
        const allPhysicians = physicianSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter by organization first using smart inference for legacy documents
        const orgFilteredPhysicians = allPhysicians.filter(physician => matchesOrg(physician, organization));
        
        // Filter physicians that belong to the ACTIVE practice (from toggle) and are active for that practice
        const filteredPhysicians = orgFilteredPhysicians.filter(physician => {
          // Check if physician has the new practices array format
          if (physician.practices && Array.isArray(physician.practices)) {
            const practiceEntry = physician.practices.find(p => p.name === activePractice);
            return practiceEntry && practiceEntry.status === 'Active';
          }
          // Fallback for old format (single practice)
          return physician.practice === activePractice && physician.status === 'Active';
        });

        // Sort by last name, then first name
        filteredPhysicians.sort((a, b) => {
          const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
          if (lastNameCompare !== 0) return lastNameCompare;
          return (a.firstName || '').localeCompare(b.firstName || '');
        });

        setPhysicians(filteredPhysicians);
      } catch (error) {
        console.error('Error fetching physicians:', error);
        setPhysicians([]);
      } finally {
        setIsLoadingPhysicians(false);
      }
    };

    fetchPhysicians();
  }, [activePractice, userRole, organization]);

  // Fetch existing procedures when procedure selection changes
  useEffect(() => {
    const fetchProcedures = async () => {
      // Only fetch if "existing" is selected
      if (patientInfo.procedureSelection !== 'existing') {
        setExistingProcedures([]);
        return;
      }

      // Don't fetch if active practice not loaded yet (unless System Admin)
      if (!activePractice && userRole !== 'System Administrator') {
        setExistingProcedures([]);
        setIsLoadingProcedures(false);
        return;
      }

      setIsLoadingProcedures(true);
      try {
        const proceduresRef = collection(db, 'Procedures');
        // Build query based on user role
        let querySnapshot;
        try {
          if (userRole === 'System Administrator') {
            // System Admins can see all procedures
            const q = query(proceduresRef, where('assigned', '==', false));
            querySnapshot = await getDocs(q);
          } else if (activePractice) {
            // Regular users and Practice Admins see only their ACTIVE practice's procedures
            // Note: This requires a composite index for (assigned, practice)
            // If index doesn't exist, it will fall back to client-side filtering
            try {
              const q = query(
                proceduresRef,
                where('assigned', '==', false),
                where('practice', '==', activePractice)
              );
              querySnapshot = await getDocs(q);
            } catch (indexError) {
              // Fallback: query by assigned only, filter practice client-side
              console.warn('Composite index not found, using client-side practice filtering');
              const q = query(proceduresRef, where('assigned', '==', false));
              querySnapshot = await getDocs(q);
            }
          } else {
            // No practice assigned, return empty
            setExistingProcedures([]);
            setIsLoadingProcedures(false);
            return;
          }
        } catch (err) {
          // Fallback: fetch all if index not available or query fails, then filter client-side
          console.warn('Error with query, fetching all procedures and filtering client-side:', err);
          querySnapshot = await getDocs(proceduresRef);
        }

        const procedures = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Include completed, unassigned procedures
          const isCompleted = data.completed && data.status === 'completed';
          const isUnassigned = data.assigned === false || data.assigned === 'N' || data.assigned === undefined || data.assigned === null;
          // Filter by ACTIVE practice if not System Administrator
          const matchesPractice = userRole === 'System Administrator' || !activePractice || data.practice === activePractice;
          // Filter by organization using smart inference for legacy documents
          const docMatchesOrg = matchesOrg(data, organization);
          
          if (isCompleted && isUnassigned && matchesPractice && docMatchesOrg) {
            procedures.push({
              id: doc.id,
              procedureId: data.procedureId || doc.id,
              procedureType: data.procedureType || 'Unknown',
              patientID: data.patientID || '',
              userEmail: data.userEmail || '',
              startTime: data.startTime,
              endTime: data.endTime,
              durationFormatted: data.durationFormatted || 'N/A',
              practice: data.practice || ''
            });
          }
        });

        // Sort by start time (most recent first)
        procedures.sort((a, b) => {
          const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
          const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
          return timeB - timeA;
        });

        setExistingProcedures(procedures);
      } catch (error) {
        console.error('Error fetching procedures:', error);
        setExistingProcedures([]);
      } finally {
        setIsLoadingProcedures(false);
      }
    };

    // Debounce the fetch
    const timer = setTimeout(() => {
      fetchProcedures();
    }, 500);

    return () => clearTimeout(timer);
  }, [patientInfo.procedureSelection, activePractice, userRole, organization]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatientInfo(prev => {
      const updated = { ...prev, [name]: value };
      // Reset procedure ID and type when switching to new entry
      if (name === 'procedureSelection' && value === 'new') {
        updated.procedureId = '';
        updated.procedureType = '';
      }
      // Reset procedure ID and type when switching to existing but no procedure selected yet
      if (name === 'procedureSelection' && value === 'existing') {
        updated.procedureId = '';
        updated.procedureType = '';
      }
      // Handle physician selection - set both ID and name
      if (name === 'physicianId') {
        if (value) {
          const selectedPhysician = physicians.find(p => p.id === value);
          if (selectedPhysician) {
            updated.physicianName = `${selectedPhysician.firstName} ${selectedPhysician.lastName}`;
          }
        } else {
          updated.physicianName = '';
        }
      }
      return updated;
    });
  };

  const handleProcedureSelect = async (procedureId) => {
    // Find the selected procedure and populate patientID and procedureType
    const selectedProcedure = existingProcedures.find(p => p.procedureId === procedureId);
    
    if (selectedProcedure) {
      setPatientInfo(prev => ({
        ...prev,
        procedureId: procedureId,
        patientID: selectedProcedure.patientID || '',
        procedureType: selectedProcedure.procedureType || ''
      }));
    } else {
      // If procedure not found in list, fetch it from Firestore
      try {
        const proceduresRef = collection(db, 'Procedures');
        const q = query(proceduresRef, where('procedureId', '==', procedureId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const procedureDoc = querySnapshot.docs[0];
          const procedureData = procedureDoc.data();
          
          setPatientInfo(prev => ({
            ...prev,
            procedureId: procedureId,
            patientID: procedureData.patientID || '',
            procedureType: procedureData.procedureType || ''
          }));
        } else {
          setPatientInfo(prev => ({
            ...prev,
            procedureId: procedureId,
            procedureType: ''
          }));
        }
      } catch (error) {
        console.error('Error fetching procedure details:', error);
    setPatientInfo(prev => ({
      ...prev,
          procedureId: procedureId,
          procedureType: ''
        }));
      }
    }
  };

  // Format date/time for display
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require patient ID
    if (!patientInfo.patientID) {
      return;
    }

    setIsSubmitting(true);
    
    // Prepare submission data (procedure selection commented out - always 'new')
    const submissionData = {
      patientID: patientInfo.patientID,
      procedureId: null, // Procedure linking disabled
      procedureType: null,
      procedureSelection: 'new', // Always 'new' since procedure selection is disabled
      physicianId: patientInfo.physicianId || null,
      physicianName: patientInfo.physicianName || null
    };
    
    // Small delay to show loading state
    setTimeout(() => {
      onSubmit(submissionData);
      setIsSubmitting(false);
    }, 100);
  };

  // Get procedure type display name
  const getProcedureTypeName = (procedureType) => {
    const procedureNames = {
      'extraction': 'Tooth Extraction',
      'implant': 'Dental Implant',
      'biopsy': 'Oral Biopsy',
      'cyst': 'Cyst Removal',
      'fracture': 'Fracture Repair',
      'orthognathic': 'Orthognathic Surgery',
      'tmj': 'TMJ Surgery',
      'reconstruction': 'Facial Reconstruction',
      'other': 'Other Procedure'
    };
    return procedureNames[procedureType] || procedureType || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header Card */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-indigo-100">
            <FaUser className="size-8 text-indigo-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Patient Information
          </h1>
          <p className="mt-2 text-base text-gray-600">
            {isSuccessWorkflow
              ? 'Enter patient ID and optionally link to an existing procedure, then continue to describe the outcome.'
              : 'Please provide the patient\'s basic information to continue'}
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <div className="px-6 py-8 sm:px-10 sm:py-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Patient ID Field */}
              <div>
                <label htmlFor="patientID" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FaUser className="size-4 text-gray-400" />
                  Patient ID
                  <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="patientID"
                    name="patientID"
                    type="text"
                    value={patientInfo.patientID}
                    onChange={handleChange}
                    required
                    placeholder="Enter patient ID"
                    className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
              </div>

              {/* Physician Selection */}
              <div>
                <label htmlFor="physicianId" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FaUserMd className="size-4 text-gray-400" />
                  Attending Physician
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Select the physician responsible for this patient's care (optional)
                </p>
                <div className="mt-2">
                  {isLoadingPhysicians ? (
                    <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                      <svg className="animate-spin size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading physicians...
                    </div>
                  ) : physicians.length === 0 ? (
                    <div className="rounded-md bg-gray-50 px-3 py-2.5 text-sm text-gray-500">
                      No physicians available. Contact your practice administrator to add physicians.
                    </div>
                  ) : (
                    <select
                      id="physicianId"
                      name="physicianId"
                      value={patientInfo.physicianId}
                      onChange={handleChange}
                      className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    >
                      <option value="">Select a physician (optional)</option>
                      {physicians.map((physician) => (
                        <option key={physician.id} value={physician.id}>
                          {physician.lastName}, {physician.firstName}
                          {physician.specialty ? ` - ${physician.specialty}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* PROCEDURE SELECTION SECTION - COMMENTED OUT FOR FUTURE USE
              <div className="border-t border-gray-200 pt-6">
                <label className="block text-sm font-semibold text-gray-900 mb-4">
                  Procedure Association
                </label>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      id="procedure-new"
                      name="procedureSelection"
                      type="radio"
                      value="new"
                      checked={patientInfo.procedureSelection === 'new'}
                      onChange={handleChange}
                      className="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label htmlFor="procedure-new" className="ml-3 flex items-center gap-2 text-sm font-medium text-gray-900">
                      <FaPlus className="size-4 text-gray-400" />
                      New Entry (No Procedure Attached)
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="procedure-existing"
                      name="procedureSelection"
                      type="radio"
                      value="existing"
                      checked={patientInfo.procedureSelection === 'existing'}
                      onChange={handleChange}
                      className="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label htmlFor="procedure-existing" className="ml-3 flex items-center gap-2 text-sm font-medium text-gray-900">
                      <FaLink className="size-4 text-gray-400" />
                      Choose Existing Procedure
                    </label>
                  </div>
                </div>

                {patientInfo.procedureSelection === 'existing' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Procedure
                    </label>
                    {isLoadingProcedures ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-sm text-gray-500">Loading procedures...</div>
                      </div>
                    ) : existingProcedures.length === 0 ? (
                      <div className="rounded-lg bg-gray-50 px-4 py-6 text-center">
                        <p className="text-sm text-gray-500">
                          No unassigned procedures found. All completed procedures have been assigned.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                                  Patient ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                                  User Email
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                                  Start Time
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                                  Procedure Type
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {existingProcedures.map((proc) => (
                                <tr
                                  key={proc.id}
                                  onClick={() => handleProcedureSelect(proc.procedureId)}
                                  className={`cursor-pointer transition-colors ${
                                    patientInfo.procedureId === proc.procedureId
                                      ? 'bg-indigo-50 hover:bg-indigo-100'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <td className="whitespace-nowrap px-6 py-3 text-sm font-medium text-gray-900">
                                    {proc.patientID || 'N/A'}
                                  </td>
                                  <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-600">
                                    {proc.userEmail || 'N/A'}
                                  </td>
                                  <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-600">
                                    {formatDateTime(proc.startTime)}
                                  </td>
                                  <td className="px-6 py-3 text-sm text-gray-600">
                                    <span className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
                                      {getProcedureTypeName(proc.procedureType)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {patientInfo.procedureId && (
                          <div className="border-t border-gray-200 bg-indigo-50 px-4 py-2">
                            <p className="text-xs font-medium text-indigo-700">
                              Selected: {existingProcedures.find(p => p.procedureId === patientInfo.procedureId)?.procedureId || 'N/A'}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              END PROCEDURE SELECTION SECTION */}

              {/* Submit Button */}
              <div className="flex items-center justify-center gap-x-4 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting || !patientInfo.patientID}
                  className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <>
                        <FaArrowRight aria-hidden="true" className="-ml-0.5 size-5" />
                        <span>Continue to Form</span>
                      </>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientInfoForm;
